import Class from '@/Class';

/**
 * Provides utility functions related to waiting for DOM elements
 */
export default class ElementAwaiter extends Class {
    private mutationObservers: MutationObserver[] = [];

    constructor() {
        super();

        this.isDisposable = true;
    }

    /**
     * Waits until at least a certain number of elements have been added to the DOM under the container element.
     * Example use case: Activating a custom scrollbar on an external component that gets rendered asynchronously.
     * @param $container The container element in which to search for child elements.
     * @param selector The JQuery selector which is used to identify added elements under the container element.
     * @param amount The amount of matching elements that needs to be added in order for the promise to complete.
     */
    public waitUntilRenderedAsync($container: JQuery, selector: JQuery.Selector | Element | JQuery<HTMLElement>, amount: number = 1): Promise<JQuery<Node>[]> {
        return new Promise(resolve => {
            const result: JQuery<Node>[] = [];
            $container.find(selector as any).each((_, e) => {
                result.push($(e));
                amount--;
            });
            if (amount <= 0) {
                resolve(result);
                return;
            }

            const mutationObserver = new MutationObserver(mutations => {
                const addedNodes: JQuery<Node>[] = [];
                mutations.forEach(mutation => mutation.addedNodes.forEach(n => addedNodes.push($(n))));
                addedNodes
                    .map($e => $e.is(selector) ? $e : $e.find(selector as any))
                    .filter($e => $e.length > 0)
                    .forEach($e => {
                        result.push($e);
                        amount--;
                    });

                if (amount <= 0) {
                    this.removeMutationObserver(mutationObserver);
                    resolve(result);
                }
            });
            this.mutationObservers.push(mutationObserver);
            mutationObserver.observe($container[0], { childList: true, subtree: true });
        });
    }

    private removeMutationObserver(mutationObserver: MutationObserver) {
        mutationObserver.disconnect();

        if (this.isDisposed)
            return;

        const index = this.mutationObservers.indexOf(mutationObserver);
        if (index >= 0) {
            this.mutationObservers.splice(index, 1);
        }
    }

    public dispose(): void {
        for (const observer of this.mutationObservers) {
            observer.disconnect();
        }
        super.dispose();
    }
}